import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Upload, FileText, ExternalLink, Eye, EyeOff } from "lucide-react";

const DOC_TYPES = [
  "COA",
  "Certification",
  "Specification Sheet",
  "Lab Report",
  "Compliance Document",
  "Facility Audit",
  "Other",
];

const PRODUCT_OPTIONS = [
  { slug: "purified-water", label: "Purified Water" },
  { slug: "alkaline-water", label: "Alkaline Water" },
  { slug: "spring-water", label: "Spring Water" },
  { slug: "sparkling-water", label: "Sparkling Water" },
  { slug: "cbd-water", label: "CBD Water" },
  { slug: "cbd-water-pets", label: "CBD Water for Pets" },
];

const emptyForm = {
  title: "",
  description: "",
  doc_type: "Certification",
  effective_date: "",
  expiration_date: "",
  upload_id: "",
  file_url: "",
  visibility: "public",
  product_slugs: [],
  badge_slugs: [],
  status: "active",
  display_order: 100,
};

const AdminTrustDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [badges, setBadges] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState("");

  const load = () => {
    api.get("/admin/trust-documents").then((r) => setDocuments(r.data.documents || [])).catch(() => toast.error("Failed to load documents"));
    api.get("/admin/compliance-badges").then((r) => setBadges(r.data.badges || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(emptyForm);
    setUploadedName("");
    setEditing(null);
    setShowNew(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      title: d.title || "",
      description: d.description || "",
      doc_type: d.doc_type || "Certification",
      effective_date: d.effective_date || "",
      expiration_date: d.expiration_date || "",
      upload_id: d.upload_id || "",
      file_url: d.file_url || "",
      visibility: d.visibility || "public",
      product_slugs: d.product_slugs || [],
      badge_slugs: d.badge_slugs || [],
      status: d.status || "active",
      display_order: d.display_order ?? 100,
    });
    setUploadedName("");
  };

  const close = () => { setEditing(null); setShowNew(false); setForm(emptyForm); setUploadedName(""); };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/uploads/document?purpose=trust-document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, upload_id: r.data.id, file_url: "" }));
      setUploadedName(r.data.filename || file.name);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.upload_id && !form.file_url) {
      return toast.error("Upload a file or paste an external URL");
    }
    try {
      const payload = { ...form };
      // Strip empty strings to keep the doc tidy
      if (!payload.effective_date) delete payload.effective_date;
      if (!payload.expiration_date) delete payload.expiration_date;
      if (!payload.file_url) delete payload.file_url;
      if (!payload.upload_id) delete payload.upload_id;
      if (editing) {
        await api.patch(`/admin/trust-documents/${editing.id}`, payload);
      } else {
        await api.post("/admin/trust-documents", payload);
      }
      toast.success("Saved");
      close();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    try {
      await api.delete(`/admin/trust-documents/${d.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleArrayVal = (key, val) => {
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter((s) => s !== val) : [...f[key], val],
    }));
  };

  const docLink = (d) => d.upload_id ? `/api/uploads/${d.upload_id}` : d.file_url;
  const isExpired = (d) => d.expiration_date && new Date(d.expiration_date) < new Date();

  return (
    <AdminLayout>
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Trust Documents</div>
          <h1 className="text-3xl font-bold tracking-tight text-[var(--ft-black)]">Documentation Library</h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Upload COAs, certifications, spec sheets, and lab reports. Attach each document to the products and
            compliance badges it covers. Public documents appear on the public Trust &amp; Certifications page.
          </p>
        </div>
        <button onClick={openNew} className="ft-btn-primary" data-testid="new-trust-doc-btn"><Plus size={16}/> New Document</button>
      </div>

      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table" data-testid="trust-docs-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Effective</th>
              <th>Expires</th>
              <th>Visibility</th>
              <th>Assigned</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {documents.length === 0 && (
              <tr><td colSpan={7} className="text-center text-[var(--ft-muted)] py-12">No trust documents yet. Upload your first COA or certification.</td></tr>
            )}
            {documents.map((d) => (
              <tr key={d.id} data-testid={`trust-doc-row-${d.id}`}>
                <td>
                  <div className="font-semibold text-[var(--ft-black)] flex items-center gap-2">
                    <FileText size={14} className="text-[var(--ft-gold)]"/> {d.title}
                    {isExpired(d) && (
                      <span className="text-[0.6rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-700" title="Expiration date has passed">Expired</span>
                    )}
                  </div>
                  {d.description && <div className="text-xs text-[var(--ft-muted)] mt-1 line-clamp-2 max-w-md">{d.description}</div>}
                </td>
                <td className="text-xs"><span className="px-2 py-1 rounded-full border border-[var(--ft-border)]">{d.doc_type}</span></td>
                <td className="text-xs">{d.effective_date || <span className="italic text-[var(--ft-muted)]">—</span>}</td>
                <td className="text-xs">{d.expiration_date || <span className="italic text-[var(--ft-muted)]">—</span>}</td>
                <td>
                  <span className={`inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-full ${d.visibility === "public" ? "bg-[var(--ft-gold)] text-[var(--ft-black)]" : "bg-[var(--ft-charcoal)] text-white/80"}`}>
                    {d.visibility === "public" ? <Eye size={10}/> : <EyeOff size={10}/>} {d.visibility}
                  </span>
                </td>
                <td className="text-xs">
                  <div>{(d.product_slugs || []).length} product{d.product_slugs?.length === 1 ? "" : "s"}</div>
                  <div>{(d.badge_slugs || []).length} badge{d.badge_slugs?.length === 1 ? "" : "s"}</div>
                </td>
                <td>
                  <div className="flex gap-1.5 items-center">
                    {docLink(d) && (
                      <a href={docLink(d)} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)] inline-flex items-center gap-1" data-testid={`trust-doc-open-${d.id}`}>
                        <ExternalLink size={12}/> Open
                      </a>
                    )}
                    <button onClick={() => openEdit(d)} className="text-xs px-2 py-1 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)]" data-testid={`trust-doc-edit-${d.id}`}>Edit</button>
                    <button onClick={() => remove(d)} className="p-1 rounded-full text-[var(--ft-muted)] hover:text-red-600" data-testid={`trust-doc-del-${d.id}`}><Trash2 size={12}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(editing || showNew) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold tracking-tight">{editing ? "Edit Trust Document" : "New Trust Document"}</h2>
              <button onClick={close}><X size={20}/></button>
            </div>
            <form onSubmit={save} className="space-y-4" data-testid="trust-doc-form">
              <div><label className="ft-label">Title*</label><input required className="ft-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="td-title" placeholder="e.g. cGMP Facility Certificate — Q1 2026" /></div>
              <div><label className="ft-label">Description</label><textarea rows={2} className="ft-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="td-desc" placeholder="Short summary of what this document covers" /></div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="ft-label">Document Type*</label>
                  <select required className="ft-input" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} data-testid="td-type">
                    {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="ft-label">Visibility*</label>
                  <select className="ft-input" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="td-visibility">
                    <option value="public">Public (shown on Trust page)</option>
                    <option value="internal">Internal Only (hidden from public)</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="ft-label">Effective Date</label><input type="date" className="ft-input" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="td-effective" /></div>
                <div><label className="ft-label">Expiration Date (optional)</label><input type="date" className="ft-input" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} data-testid="td-expiration" /></div>
              </div>

              <div>
                <label className="ft-label">File*</label>
                <div className="flex gap-2 items-start">
                  <label className="ft-btn-ghost cursor-pointer inline-flex items-center gap-1.5 whitespace-nowrap" data-testid="td-upload">
                    <Upload size={14}/>
                    <span className="text-xs">{uploading ? "Uploading…" : "Upload PDF / Image"}</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="application/pdf,image/*"
                      disabled={uploading}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }}
                    />
                  </label>
                  {(form.upload_id || uploadedName) && (
                    <div className="flex-1 flex items-center gap-2 text-xs text-[var(--ft-muted)] bg-[var(--ft-light-gray)] rounded-lg px-3 py-2" data-testid="td-uploaded-name">
                      <FileText size={12} className="text-[var(--ft-gold)]"/>
                      <span className="truncate">{uploadedName || `Uploaded: ${form.upload_id}`}</span>
                      {form.upload_id && (
                        <a href={`/api/uploads/${form.upload_id}`} target="_blank" rel="noopener noreferrer" className="text-[var(--ft-gold)] font-semibold inline-flex items-center gap-1">
                          <ExternalLink size={11}/> View
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-xs text-[var(--ft-muted)] mt-2">…or paste an external URL:</div>
                <input className="ft-input mt-1" placeholder="https://…" value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value, upload_id: e.target.value ? "" : form.upload_id })} data-testid="td-file-url" />
              </div>

              <div>
                <label className="ft-label">Applies to Products ({form.product_slugs.length} selected)</label>
                <div className="grid md:grid-cols-2 gap-2 mt-1">
                  {PRODUCT_OPTIONS.map((p) => (
                    <label key={p.slug} className={`flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border transition-all ${form.product_slugs.includes(p.slug) ? "border-[var(--ft-gold)] bg-[var(--ft-gold)]/8" : "border-[var(--ft-border)]"}`} data-testid={`td-product-${p.slug}`}>
                      <input type="checkbox" checked={form.product_slugs.includes(p.slug)} onChange={() => toggleArrayVal("product_slugs", p.slug)} />
                      <div className="text-sm">{p.label}</div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="ft-label">Applies to Certifications ({form.badge_slugs.length} selected)</label>
                <div className="grid md:grid-cols-2 gap-2 mt-1">
                  {badges.filter((b) => b.status === "active").map((b) => (
                    <label key={b.slug} className={`flex items-center gap-2 text-sm cursor-pointer p-2 rounded-lg border transition-all ${form.badge_slugs.includes(b.slug) ? "border-[var(--ft-gold)] bg-[var(--ft-gold)]/8" : "border-[var(--ft-border)]"}`} data-testid={`td-badge-${b.slug}`}>
                      <input type="checkbox" checked={form.badge_slugs.includes(b.slug)} onChange={() => toggleArrayVal("badge_slugs", b.slug)} />
                      <div className="text-sm">{b.label}</div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><label className="ft-label">Status</label><select className="ft-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="active">Active</option><option value="archived">Archived</option></select></div>
                <div><label className="ft-label">Display Order</label><input type="number" className="ft-input" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })} /></div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-[var(--ft-border)]">
                <button type="button" onClick={close} className="ft-btn-ghost">Cancel</button>
                <button type="submit" className="ft-btn-primary" data-testid="td-save">Save Document</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminTrustDocuments;
